// 기업회원 엑셀 일괄 업로드 파싱.
// 바디스위치_기업회원양식.xlsx 헤더 고정(14열): 지점명·대표자명·휴대폰·이메일·대표번호·사업자번호·업태·주소·상세주소·은행명·계좌번호·예금주·아이디·비밀번호

const COLUMNS = {
  branchName: '지점명',
  representativeName: '대표자명',
  phone: '휴대폰',
  email: '이메일',
  representativeNumber: '대표번호',
  businessNumber: '사업자번호',
  businessType: '업태',
  address: '주소',
  addressDetail: '상세주소',
  bankName: '은행명',
  accountNumber: '계좌번호',
  accountHolder: '예금주',
  username: '아이디',
  password: '비밀번호',
};

// 지점명이 '-' 이거나 '지부'만 있고 괄호가 없으면 자동 제외(작성안내 규칙).
const isAutoExcludedBranchName = value => value === '-' || value === '지부';

// 센터 상세 화면의 은행명 드롭다운(constants.js의 koreanBanks)은 정식 명칭만 허용한다.
// 엑셀엔 "국민", "신한"처럼 줄여 적는 경우가 많아, 알려진 축약형은 정식 명칭으로 바꿔준다.
const CANONICAL_BANK_NAMES = new Set([
  'KB국민은행', '신한은행', '우리은행', '하나은행', 'IBK기업은행', 'NH농협은행', '카카오뱅크', '토스뱅크',
  '케이뱅크', 'SC제일은행', '씨티은행', 'DGB대구은행', 'BNK부산은행', 'BNK경남은행', '광주은행', '전북은행',
  '제주은행', '수협은행', '신협', '새마을금고', '우체국', 'KDB산업은행', 'SBI저축은행',
]);

const BANK_NAME_ALIASES = {
  '국민': 'KB국민은행', kb: 'KB국민은행',
  '신한': '신한은행',
  '우리': '우리은행',
  '하나': '하나은행',
  '기업': 'IBK기업은행', ibk: 'IBK기업은행',
  '농협': 'NH농협은행', nh: 'NH농협은행',
  '카카오': '카카오뱅크',
  '토스': '토스뱅크',
  '케이': '케이뱅크',
  '제일': 'SC제일은행', sc: 'SC제일은행',
  '씨티': '씨티은행',
  '대구': 'DGB대구은행', dgb: 'DGB대구은행',
  '부산': 'BNK부산은행',
  '경남': 'BNK경남은행',
  '광주': '광주은행',
  '전북': '전북은행',
  '제주': '제주은행',
  '수협': '수협은행',
  '신협': '신협',
  '새마을': '새마을금고', '새마을금고': '새마을금고',
  '우체국': '우체국',
  '산업': 'KDB산업은행', kdb: 'KDB산업은행',
  sbi: 'SBI저축은행',
};

const normalizeBankName = raw => {
  if (!raw) return '';
  if (CANONICAL_BANK_NAMES.has(raw)) return raw;
  const key = raw.replace(/은행|뱅크/g, '').trim().toLowerCase();
  return BANK_NAME_ALIASES[key] || raw;
};

// 공백/'*'(필수 표시로 붙이는 경우가 있음) 제거 후 비교.
const normalizeHeader = value => String(value).replace(/[\s*]/g, '');

// 헤더를 컬럼 라벨에 매칭한다. 정확히 일치하는 헤더를 먼저 전부 찾고(1차),
// 그래도 못 찾은 라벨만 '포함' 관계로 다시 찾는다(2차, 예: '종목/업태' 헤더가 '업태' 라벨을 포함).
// 2차에서 이미 다른 라벨에 매칭된 헤더는 후보에서 제외해서 '주소'가 '상세주소'를 잘못 가져가는 걸 막는다.
const matchHeaderKeys = (keys, columns) => {
  const result = {};
  const used = new Set();

  Object.entries(columns).forEach(([field, label]) => {
    const key = keys.find(k => !used.has(k) && normalizeHeader(k) === normalizeHeader(label));
    if (key) {
      result[field] = key;
      used.add(key);
    }
  });

  Object.entries(columns).forEach(([field, label]) => {
    if (result[field]) return;
    const key = keys.find(k => !used.has(k) && normalizeHeader(k).includes(normalizeHeader(label)));
    if (key) {
      result[field] = key;
      used.add(key);
    }
  });

  return result;
};

const normalizePhone = raw => {
  const digits = String(raw ?? '').replace(/\D/g, '');
  if (digits.length !== 10 && digits.length !== 11) return '';
  const formatted =
    digits.length === 11
      ? digits.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3')
      : digits.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
  return /^010-\d{4}-\d{4}$/.test(formatted) ? formatted : '';
};

const requiredFieldErrors = row => {
  const errors = [];
  if (!row.branchName) errors.push('지점명 없음');
  if (!row.representativeName) errors.push('대표자명 없음');
  if (!row.businessNumber) errors.push('사업자번호 없음');
  if (!row.businessType) errors.push('업태 없음');
  if (!row.address) errors.push('주소 없음');
  if (!row.username) errors.push('아이디 없음');
  if (!row.password) errors.push('비밀번호 없음');
  return errors;
};

// rows: XLSX.utils.sheet_to_json(sheet, { defval: '' }) 결과
// 반환: { missingColumns, excluded: [{rowNumber, branchName}], rows: [파싱된 행] }
export function parseCorpExcelRows(rows) {
  if (!rows.length) return { missingColumns: true, excluded: [], rows: [] };

  const keys = Object.keys(rows[0]);
  const headerKeys = matchHeaderKeys(keys, COLUMNS);
  const missingColumns = Object.keys(COLUMNS).some(field => !headerKeys[field]);
  if (missingColumns) return { missingColumns: true, excluded: [], rows: [] };

  const excluded = [];
  const parsed = [];

  rows.forEach((raw, index) => {
    const rowNumber = index + 2; // 헤더가 1행이므로 데이터는 2행부터
    const get = field => String(raw[headerKeys[field]] ?? '').trim();

    const branchName = get('branchName');
    if (isAutoExcludedBranchName(branchName)) {
      excluded.push({ rowNumber, branchName });
      return;
    }

    const row = {
      rowNumber,
      branchName,
      representativeName: get('representativeName'),
      phoneNumber: normalizePhone(get('phone')),
      email: get('email'),
      representativeNumber: get('representativeNumber'),
      businessNumber: get('businessNumber'),
      businessType: get('businessType'),
      address: get('address'),
      addressDetail: get('addressDetail'),
      bankName: normalizeBankName(get('bankName')),
      accountNumber: get('accountNumber'),
      accountHolder: get('accountHolder'),
      username: get('username'),
      password: get('password'),
    };

    row.errors = requiredFieldErrors(row);
    parsed.push(row);
  });

  return { missingColumns: false, excluded, rows: parsed };
}
